import { redirect } from '@tanstack/react-router'
import { authService, type User } from './auth-service'
import { queryClient } from './query-client'
import { userQueryKey } from '@/context/auth'

/**
 * Used in route `beforeLoad`. Resolves the current user (cached for 5 min in
 * the QueryClient) and redirects to /login if unauthenticated. Pages should
 * never render in an "unknown auth" state thanks to this.
 */
export async function requireAuth({ location }: { location: { href: string } }): Promise<User> {
  const user = await queryClient.ensureQueryData<User | null>({
    queryKey: userQueryKey,
    queryFn: () => authService.fetchUser(),
    staleTime: 5 * 60_000,
  })

  if (!user) {
    throw redirect({
      to: '/login',
      search: { redirect: location.href },
    })
  }

  return user
}

/**
 * Inverse of requireAuth: kicks an already-logged-in user out of /login,
 * /register, /forgot-password, /reset-password pages back to dashboard.
 */
export async function requireGuest() {
  const user = await queryClient.ensureQueryData<User | null>({
    queryKey: userQueryKey,
    queryFn: () => authService.fetchUser(),
    staleTime: 5 * 60_000,
  })

  if (user) {
    throw redirect({ to: '/dashboard' })
  }
}
